import { PROJECT_MEDIA_TYPES } from 'consts'
import { useEffect, useMemo, useState } from 'react'
import { videoThumbsService } from 'services/video-thumbs'
import { IMediaItem, MediaStatus } from 'types/media'

const videoTypes = [PROJECT_MEDIA_TYPES.video, PROJECT_MEDIA_TYPES.aiVideo]
export interface ITimelineMediaFrame {
  timestampSec: number
  url: string
}

/** All cached whole-second thumbs for the source file (same filmstrip width as main: originalDuration × pps, panned by trimStart). */
const mapToSortedFramesForFullFilmstrip = (
  map: Map<number, string>,
  originalDuration: number | undefined
): ITimelineMediaFrame[] => {
  const end =
    originalDuration !== undefined && Number.isFinite(originalDuration)
      ? Math.ceil(originalDuration)
      : Number.POSITIVE_INFINITY
  return Array.from(map.entries())
    .filter(([ts]) => ts >= 0 && ts < end)
    .map(([timestampSec, url]) => ({ timestampSec, url }))
    .sort((a, b) => a.timestampSec - b.timestampSec)
}

export const useMediaItemFrames = (mediaItem: IMediaItem) => {
  const { assetId, meta, type } = mediaItem
  const isVideo = videoTypes.includes(type)
  const isReady = meta.status === MediaStatus.OK
  const originalDuration = meta.originalDuration
  const path = useMemo(
    () => meta.previewPath || meta.path || '',
    [meta.previewPath, meta.path]
  )

  const [framesByTime, setFramesByTime] = useState<ITimelineMediaFrame[]>(
    () => {
      if (!isVideo) return []
      return mapToSortedFramesForFullFilmstrip(
        videoThumbsService.getCachedUrls(assetId),
        originalDuration
      )
    }
  )

  const frames = useMemo(
    () => framesByTime.map(frame => frame.url),
    [framesByTime]
  )

  useEffect(() => {
    if (!isVideo) {
      if (path) {
        setFramesByTime([{ timestampSec: 0, url: path }])
      }
      return
    }

    if (!isReady) return

    return videoThumbsService.subscribe(assetId, map => {
      const entries = Array.from(map.entries())
        .filter(([timestamp]) => {
          const end =
            originalDuration !== undefined && Number.isFinite(originalDuration)
              ? Math.ceil(originalDuration)
              : Number.POSITIVE_INFINITY
          return timestamp >= 0 && timestamp < end
        })
        .sort(([a], [b]) => a - b)
      setFramesByTime(
        entries.map(([timestampSec, url]) => ({ timestampSec, url }))
      )
    })
  }, [assetId, isVideo, isReady, path, originalDuration])

  return { frames, framesByTime, setFramesByTime }
}
